/**
 * Web Push 구독 클라이언트 (PWA Phase 3).
 *
 * 흐름:
 *   1) getVapidPublicKey() — 백엔드에서 공개키 + 활성화 여부 받음
 *   2) ensurePermission() — Notification 권한 요청 (default → granted/denied)
 *   3) subscribe() — PushManager 구독 + 백엔드 저장
 *   4) unsubscribe() — PushManager 해제 + 백엔드 삭제
 *   5) currentSubscription() — 현재 구독 상태 확인 (UI 토글 초기값용)
 */

const BASE = '/api';

export interface VapidStatus {
  enabled: boolean;
  publicKey: string | null;
}

export async function getVapidPublicKey(): Promise<VapidStatus> {
  const res = await fetch(`${BASE}/push/vapid-public-key`, { credentials: 'include' });
  const json = await res.json();
  if (!json.ok) throw new Error(json.error?.message ?? '공개키 조회 실패');
  return json.data as VapidStatus;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function ensurePermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

/** base64url → Uint8Array (PushManager.applicationServerKey 형식 변환) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function subscribe(publicKey: string): Promise<PushSubscription> {
  if (!isPushSupported()) throw new Error('이 브라우저는 알림을 지원하지 않습니다');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // TS PushSubscriptionOptionsInit 의 BufferSource 는 ArrayBuffer 전용. Uint8Array.buffer 캐스팅으로 우회.
      applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
    });
  }
  // 백엔드 저장 — endpoint UNIQUE → upsert
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  const res = await fetch(`${BASE}/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      userAgent: navigator.userAgent.slice(0, 500),
    }),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`구독 저장 실패 (${res.status}): ${txt}`);
  }
  return sub;
}

export async function unsubscribe(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  // 백엔드 먼저 삭제 (실패해도 클라이언트 unsubscribe 는 진행)
  try {
    await fetch(`${BASE}/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch {
    /* ignore */
  }
  await sub.unsubscribe();
}
